import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import Logo from './Logo'
import { CATEGORIES } from '@/data/blog'

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/calculator', label: 'Calculator', end: false },
  { to: '/blog', label: 'Blog', end: false },
  { to: '/faq', label: 'FAQ', end: false },
  { to: '/about', label: 'About', end: false },
]

function Layout() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setOpen(false)
    window.scrollTo(0, 0)
  }, [pathname])

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-3 py-2 text-[15px] font-semibold transition-colors ${
      isActive ? 'bg-blueprint-50 text-blueprint-800' : 'text-steel-700 hover:bg-steel-100 hover:text-ink'
    }`

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-signal focus:px-4 focus:py-2 focus:font-bold focus:text-ink"
      >
        Skip to main content
      </a>

      <header className="sticky top-0 z-40 border-b border-steel-200 bg-white/95 backdrop-blur">
        <div className="container-page flex h-16 items-center justify-between">
          <Link to="/" aria-label="Dormer Calculator home" className="text-ink no-underline">
            <Logo />
          </Link>

          <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkClass}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/calculator" className="btn btn-primary ml-3 !py-2 text-[15px] no-underline">
              Calculate now
            </Link>
          </nav>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-ink hover:bg-steel-100 lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen(o => !o)}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <nav id="mobile-nav" aria-label="Mobile" className="border-t border-steel-200 bg-white lg:hidden">
            <div className="container-page flex flex-col gap-1 py-3">
              {NAV.map(n => (
                <NavLink key={n.to} to={n.to} end={n.end} className={linkClass}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/calculator" className="btn btn-primary mt-2 no-underline">
                Calculate now
              </Link>
            </div>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-16 bg-ink text-steel-300">
        <div className="container-page grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="no-underline" aria-label="Dormer Calculator home">
              <Logo light />
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed">
              Free dormer calculator for homeowners, builders and designers. Estimate size, materials and cost in seconds.
            </p>
          </div>
          <FooterCol title="Tools">
            <FooterLink to="/calculator">Dormer calculator</FooterLink>
            <FooterLink to="/faq">FAQ</FooterLink>
            <FooterLink to="/blog">All articles</FooterLink>
          </FooterCol>
          <FooterCol title="Learn">
            {CATEGORIES.map(c => (
              <FooterLink key={c.slug} to={`/blog/category/${c.slug}`}>
                {c.name}
              </FooterLink>
            ))}
          </FooterCol>
          <FooterCol title="Company">
            <FooterLink to="/about">About</FooterLink>
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/terms">Terms of Use</FooterLink>
            <FooterLink to="/disclaimer">Disclaimer</FooterLink>
          </FooterCol>
        </div>
        <div className="border-t border-white/10">
          <p className="container-page py-5 text-xs text-steel-400">
            &copy; {new Date().getFullYear()} Dormer Calculator (www.dormercalculator.com). Estimates are for planning only and are not a quote.
          </p>
        </div>
      </footer>
    </div>
  )
}

function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <nav aria-label={title}>
      <h2 className="mb-3 text-sm font-bold uppercase tracking-widest !text-white">{title}</h2>
      <ul className="space-y-2">{children}</ul>
    </nav>
  )
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="text-sm !text-steel-300 no-underline hover:!text-white hover:underline">
        {children}
      </Link>
    </li>
  )
}

export default Layout
